import Joi from 'joi';

import {
  type OfferActionFormValues,
  type OfferFormValues,
  type OfferLocationFormValues,
} from '@/domain/offers/types';
import { OFFER_ACTION_KINDS, OFFER_LIMITS, OFFER_SINGLE_ACTION_KINDS } from '@/domain/offers/utils';
import { translatedMessages } from '@/i18n';
import { CommonValidationSchemas } from '@/utils/schemas';

const NS = 'domain.offers.schemas';

const dateOnly = /^\d{4}-\d{2}-\d{2}$/;

const blank = Joi.string().allow('');

/** A phone number with any mask, counted by its digits. */
const phoneNumber = (pattern: RegExp, invalid: 'whatsappInvalid' | 'phoneInvalid') =>
  Joi.string()
    .pattern(pattern)
    .required()
    .error(
      translatedMessages(NS, {
        'string.empty': invalid,
        'any.required': invalid,
        'string.pattern.base': invalid,
      }),
    );

/** One button: each kind requires only its own fields; the text is optional everywhere. */
const actionSchema = Joi.object<OfferActionFormValues>({
  kind: Joi.string()
    .valid(...OFFER_ACTION_KINDS)
    .required()
    .error(translatedMessages(NS, { 'any.only': 'actionKind', 'any.required': 'actionKind' })),
  label: Joi.string()
    .trim()
    .min(OFFER_LIMITS.actionLabelMin)
    .max(OFFER_LIMITS.actionLabelMax)
    .allow('')
    .error(
      translatedMessages(NS, { 'string.min': 'actionLabelShort', 'string.max': 'actionLabelLong' }),
    ),
  url: Joi.when('kind', {
    is: 'link',
    then: Joi.string()
      .trim()
      .uri({ scheme: ['http', 'https'] })
      .max(OFFER_LIMITS.urlMax)
      .required()
      .error(
        translatedMessages(NS, {
          'string.empty': 'urlRequired',
          'any.required': 'urlRequired',
          'string.uri': 'urlInvalid',
          'string.uriCustomScheme': 'urlInvalid',
          'string.max': 'urlLong',
        }),
      ),
    otherwise: blank,
  }),
  number: Joi.when('kind', {
    switch: [
      { is: 'whatsapp', then: phoneNumber(/^\D*(\d\D*){10,13}$/, 'whatsappInvalid') },
      { is: 'phone', then: phoneNumber(/^\D*(\d\D*){8,13}$/, 'phoneInvalid') },
    ],
    otherwise: blank,
  }),
  message: Joi.when('kind', {
    is: 'whatsapp',
    then: Joi.string()
      .trim()
      .max(OFFER_LIMITS.whatsappMessageMax)
      .allow('')
      .error(translatedMessages(NS, { 'string.max': 'messageLong' })),
    otherwise: blank,
  }),
});

/** The event address plus the optional place name. */
const locationSchema = (CommonValidationSchemas.address as Joi.ObjectSchema).keys({
  label: Joi.string()
    .trim()
    .max(OFFER_LIMITS.locationLabelMax)
    .allow('')
    .error(translatedMessages(NS, { 'string.max': 'locationLabelLong' })),
}) as Joi.ObjectSchema<OfferLocationFormValues>;

/** Mirrors the API validation of offer fields (`campaigns.validations.ts`), with its copy. */
export const buildOfferSchema = () =>
  Joi.object<OfferFormValues>({
    imageUrl: Joi.string().allow(''),
    title: Joi.string()
      .trim()
      .min(OFFER_LIMITS.titleMin)
      .max(OFFER_LIMITS.titleMax)
      .required()
      .error(
        translatedMessages(NS, {
          'string.empty': 'titleRequired',
          'any.required': 'titleRequired',
          'string.min': 'titleShort',
          'string.max': 'titleLong',
        }),
      ),
    body: Joi.string()
      .trim()
      .min(OFFER_LIMITS.bodyMin)
      .max(OFFER_LIMITS.bodyMax)
      .required()
      .error(
        translatedMessages(NS, {
          'string.empty': 'bodyRequired',
          'any.required': 'bodyRequired',
          'string.min': 'bodyShort',
          'string.max': 'bodyLong',
        }),
      ),
    benefit: Joi.object({
      enabled: Joi.boolean().required(),
      code: Joi.when('enabled', {
        is: true,
        then: Joi.string()
          .trim()
          .max(OFFER_LIMITS.couponMax)
          .required()
          .error(
            translatedMessages(NS, {
              'string.empty': 'couponRequired',
              'any.required': 'couponRequired',
              'string.max': 'couponLong',
            }),
          ),
        otherwise: blank,
      }),
      terms: Joi.string()
        .allow('')
        .max(OFFER_LIMITS.couponTermsMax)
        .error(translatedMessages(NS, { 'string.max': 'couponTermsLong' })),
    }).required(),
    actions: Joi.array()
      .items(actionSchema)
      .max(OFFER_LIMITS.actionsMax)
      .unique(
        (a: OfferActionFormValues, b: OfferActionFormValues) =>
          a.kind === b.kind && OFFER_SINGLE_ACTION_KINDS.includes(a.kind),
      )
      .when('benefit.enabled', { is: false, then: Joi.array().min(1) })
      .required()
      .error(
        translatedMessages(NS, {
          'array.min': 'actionsOrCoupon',
          'array.max': 'actionsMax',
          'array.unique': 'actionDuplicate',
        }),
      ),
    locations: Joi.array()
      .items(locationSchema)
      .max(OFFER_LIMITS.locationsMax)
      .when('actions', {
        is: Joi.array().has(Joi.object({ kind: 'directions' }).unknown()),
        then: Joi.array().min(1),
      })
      .required()
      .error(
        translatedMessages(NS, { 'array.min': 'locationRequired', 'array.max': 'locationsMax' }),
      ),
    desiredAudienceText: Joi.string()
      .trim()
      .max(OFFER_LIMITS.audienceMax)
      .allow('')
      .error(translatedMessages(NS, { 'string.max': 'audienceLong' })),
    desiredStartsAt: Joi.string()
      .pattern(dateOnly)
      .allow('')
      .error(translatedMessages(NS, { 'string.pattern.base': 'dateInvalid' })),
    desiredEndsAt: Joi.string()
      .pattern(dateOnly)
      .allow('')
      .custom((value: string, helpers) => {
        const { desiredStartsAt } = helpers.state.ancestors[0] as OfferFormValues;
        return value && desiredStartsAt && value <= desiredStartsAt
          ? helpers.error('date.beforeStart')
          : value;
      })
      .error(
        translatedMessages(NS, {
          'string.pattern.base': 'dateInvalid',
          'date.beforeStart': 'endBeforeStart',
        }),
      ),
  });
